import { Link, useParams } from "react-router-dom";
import { prequalApi } from "../../api";
import { ProcError, ProcField, ProcForm, ProcGrid, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useAsyncData } from "../../hooks";

const CHECKLIST_ITEMS = [
  { key: "certOfIncorp" as const, label: "Certificate of Incorporation" },
  { key: "kraCompliance" as const, label: "KRA Tax Compliance Certificate" },
  { key: "businessPermit" as const, label: "Business Permit" },
  { key: "companyProfile" as const, label: "Company Profile" },
  { key: "pinVatCert" as const, label: "PIN/VAT Certificate" },
  { key: "signedApplication" as const, label: "Signed Application Form" },
  { key: "cr12" as const, label: "CR12 Form" },
];

const TECH_ROWS = [
  { key: "techCompanyExperience" as const, label: "Company Experience" },
  { key: "techFinancialCapability" as const, label: "Financial Capability" },
  { key: "techReferences" as const, label: "References" },
  { key: "techTechnicalCapacity" as const, label: "Technical Capacity" },
  { key: "techCertifications" as const, label: "Certifications" },
];

export default function PrequalViewPage() {
  const { id } = useParams<{ id: string }>();

  const { data: pq, loading, error } = useAsyncData(
    () => prequalApi.get(Number(id)),
    [id],
  );

  if (loading) return <ProcLoading />;
  if (error) return <ProcError message={error} />;
  if (!pq) return null;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/procurement/supplier-prequal" className="text-sm text-emerald-700 hover:underline">&larr; Back to Prequalifications</Link>
      </div>

      <ProcForm formNo="PROC-FRM-07" title="Supplier Prequalification" serialNo={pq.serialNo} date={pq.date}>
        <ProcSection title="Supplier Information">
          <ProcGrid>
            <ProcField label="Supplier Name" value={pq.supplierName} />
            <ProcField label="Contact Person" value={pq.contactPerson} />
            <ProcField label="Phone" value={pq.phone} />
            <ProcField label="Email" value={pq.email} />
          </ProcGrid>
        </ProcSection>

        <ProcSection title="Mandatory Checklist">
          <div className="space-y-2">
            {CHECKLIST_ITEMS.map(({ key, label }) => (
              <div key={key} className="flex items-center gap-3">
                <span className={pq[key] ? "text-emerald-600 font-bold" : "text-red-500 font-bold"}>{pq[key] ? "✓" : "✗"}</span>
                <span className="text-sm text-slate-700">{label}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-700">Responsive:</span>
            <ProcStatusBadge value={pq.responsive ? "YES" : "NO"} />
          </div>
        </ProcSection>

        <ProcSection title="Technical Scores">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Criterion</th>
                <th className="px-3 py-2 text-right">Score / 20</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {TECH_ROWS.map(({ key, label }) => (
                <tr key={key}>
                  <td className="px-3 py-2 text-slate-700">{label}</td>
                  <td className="px-3 py-2 text-right font-semibold">{pq[key]}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 font-bold">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right">{pq.techTotal} / 100</td>
              </tr>
            </tfoot>
          </table>
          <div className="mt-3 flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-700">Result:</span>
            <ProcStatusBadge value={pq.passed ? "PASSED" : "FAILED"} />
          </div>
        </ProcSection>
      </ProcForm>
    </div>
  );
}
